import React, { useRef, useEffect, useState, useContext, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { Chip } from 'primereact/chip';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog'; // Novo Import
import api from '../api/api';
import { FilterContext } from '../contexts/FilterContext';
import { AuthContext } from '../contexts/AuthContext';
import { PermissaoLoader } from "./PermissaoLoader";
import { permissions_check } from "../utils/rotas_permissao";
import { BrCalendar } from './BrCalendar';
import { Tag } from 'primereact/tag';

export const TabelaFalta = ({ turmaId }) => {
    const toast = useRef(null);
    const [faltasOriginais, setFaltasOriginais] = useState([]); // Lista analítica vinda da API
    const [faltasAgrupadas, setFaltasAgrupadas] = useState([]); // Lista sintética para a tabela
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [dt, setDt] = useState(null);
    const history = useHistory();
    const { permissoes } = useContext(AuthContext);
    const [sortable, setSortable] = useState('ASC');
    const [sortableCampo, setSortableCampo] = useState('id');

    // ESTADOS PARA PAGINAÇÃO E MODAL
    const [first, setFirst] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [rows, setRows] = useState(2000);
    const [displayModal, setDisplayModal] = useState(false);
    const [alunoSelecionado, setAlunoSelecionado] = useState({ nome: '', registros: [] });

    const { filter, setFilter } = useContext(FilterContext);
    const [selectedData, setSelectedData] = useState(null);
    const [selectedAtraso, setSelectedAtraso] = useState(null);

    const tiposOptions = [
        { label: 'FALTA', value: false },
        { label: 'ATRASO', value: true }
    ];

    // FUNÇÃO PARA AGRUPAR DADOS POR ALUNO
    // FUNÇÃO PARA AGRUPAR DADOS POR ALUNO COM ORDENAÇÃO
    const processarAgrupamento = useCallback((dados) => {
        const mapa = {};
        
        dados.forEach(item => {
            const alunoId = item.matricula.aluno.id;
            if (!mapa[alunoId]) {
                mapa[alunoId] = {
                    alunoId: alunoId,
                    nome: item.matricula.aluno.nome,
                    nomeSocial: item.matricula.aluno.nomeSocial,
                    totalFaltas: 0,
                    totalAtrasos: 0,
                    registros: []
                };
            }
            mapa[alunoId].registros.push(item);
            if (item.atraso) {
                mapa[alunoId].totalAtrasos++;
            } else {
                mapa[alunoId].totalFaltas++;
            }
        });

        // Converte o objeto em array e ordena por nome (A-Z)
        return Object.values(mapa).sort((a, b) => {
            return a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
        });
    }, []);

    const getFaltas = useCallback(async (page = 0, size = 2000) => {
        if (!turmaId) return;
        setIsLoading(true);
        try {
            const f = filter?.filters || {};
            let idFiltro = f.id?.value;
            if (idFiltro === turmaId) idFiltro = null;

            const pesquisa = {
                "turmaId": turmaId,
                "id": idFiltro || null,
                "nome": f.matriculaAlunoNome?.value || null,
                "obs": f.obs?.value || null,
                "dataOcorrencia": selectedData ? selectedData.toISOString().split('T')[0] + " 00:00:00.000" : null,
                "atraso": selectedAtraso !== null ? selectedAtraso : null
            };

            const response = await api.post(`falta/searchFalta/${sortable}/${sortableCampo}?page=${page}&size=${size}`, pesquisa);
            const content = response.data.content || response.data;

            setFaltasOriginais(content);
            setFaltasAgrupadas(processarAgrupamento(content));
            setTotalRecords(response.data.totalElements || content.length);
        } catch (error) {
            console.error("Erro ao carregar faltas:", error);
        }
        setIsLoading(false);
    }, [turmaId, sortable, sortableCampo, filter, selectedData, selectedAtraso, processarAgrupamento]);

    useEffect(() => {
        getFaltas(0, rows);
        setFirst(0);
    }, [turmaId, getFaltas]);

    const handleLimparClick = () => {
        setFilter({
            first: 0, rows: 2000, sortField: null, sortOrder: null,
            filters: {
                'id': { value: null, matchMode: 'contains' },
                'matriculaAlunoNome': { value: '', matchMode: 'contains' },
                'obs': { value: '', matchMode: 'contains' },
                'dataOcorrencia': { value: null, matchMode: 'equals' },
                'atraso': { value: null, matchMode: 'equals' }
            },
        });
        setSelectedData(null);
        setSelectedAtraso(null);
        setFirst(0);
    };

    const handleDeleteClick = (data) => {
        const nomeAluno = data?.matricula?.aluno?.nome || "este registro";
        confirmDialog({
            message: `Remover falta de ${nomeAluno}?`,
            header: 'Confirmação',
            acceptLabel: 'Sim', rejectLabel: 'Não',
            accept: async () => {
                setIsDeleting(true);
                try {
                    await api.delete(`falta/${data.id}`);
                    toast.current.show({ severity: 'success', summary: 'Sucesso', detail: `Falta removida` });
                    setDisplayModal(false); // Fecha o modal após deletar para atualizar a lista
                    getFaltas(Math.floor(first / rows), rows);
                } catch (error) {
                    toast.current.show({ severity: 'error', summary: 'Erro', detail: error.response?.data });
                }
                setIsDeleting(false);
            }
        });
    };

    // TEMPLATES
    const actionBodyTemplate = (rowData) => {
        const hasEditPermission = permissions_check("falta", "editar", permissoes);
        const hasDeletePermission = permissions_check("falta", "deletar", permissoes);
        return (
            <div className="actions">
                {hasEditPermission && <Button icon="pi pi-pencil" className="p-button-rounded p-mr-2" onClick={() => history.push(`/falta/edit/${rowData.id}`)} />}
                {hasDeletePermission && <Button icon="pi pi-trash" className="p-button-rounded p-button-danger" loading={isDeleting} onClick={() => handleDeleteClick(rowData)} />}
            </div>
        );
    };

    const dataOcorrenciaBodyTemplate = (rowData) => (
        <span>{rowData.dataOcorrencia ? new Date(rowData.dataOcorrencia + ' 03:00:00.000').toLocaleDateString() : ''}</span>
    );

    const tipoBodyTemplate = (rowData) => (
        <Chip label={rowData.atraso ? 'ATRASO' : 'FALTA'}
            style={{ color: 'white', backgroundColor: rowData.atraso ? '#f0b800' : '#d45555', fontWeight: 'bold' }} />
    );

    const header = (
        <div className="p-d-flex p-jc-between p-ai-center p-flex-wrap" style={{ width: '100%' }}>
            <h2 className="p-m-0">Resumo de Faltas</h2>
            <div className="p-d-flex p-ai-center">
                <PermissaoLoader
                    rota="falta"
                    acao="criar"
                    onClick={() => history.push({ pathname: '/falta/create', state: { turmaId } })}
                />
                <Button
                    label="Limpar Filtros"
                    icon="pi pi-filter-slash"
                    className="p-button-outlined p-ml-2" // p-ml-2 para dar o espaçamento entre os botões
                    onClick={() => handleLimparClick()}
                />
            </div>
        </div>
    );

    const nomeSocialTemplate = (rowData) => {
        // Só renderiza se o campo não estiver vazio
        if (!rowData.nomeSocial || rowData.nomeSocial.trim() === "") {
            return null;
        }

        return (
            <Tag
                value={rowData.nomeSocial}
                severity="info"      // "info" (azul), "success" (verde), "warning" (laranja), etc.
                rounded              // Deixa as bordas arredondadas (estilo pílula)

            />
        );
    };

    const renderHeader = () => {
        if (!alunoSelecionado) return '';

        const nomeSocial = alunoSelecionado.nomeSocial;
        const temNomeSocial = nomeSocial && nomeSocial.trim().length > 0;

        return (
            <div className="flex align-items-center gap-2">
                <span>Histórico de Ocorrências: {alunoSelecionado.nome}</span>
                {temNomeSocial && (
                    <Tag
                        value={"  " + nomeSocial}
                        severity="info"
                        // icon="pi pi-user"
                        className="ml-2"
                        style={{ fontSize: '0.8rem' }}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="card">
            <Toast ref={toast} position="bottom-right" />

            {/* TABELA PRINCIPAL (AGRUPADA) */}
            <DataTable
                value={faltasAgrupadas}
                header={header}
                loading={isLoading}
                paginator rows={rows}
                emptyMessage="Nenhuma falta encontrada nesta turma"
            >
                <Column field="nome" header="Aluno" sortable filter filterPlaceholder="Filtrar por nome" />
                <Column field="nomeSocial" header="Nome Social" body={nomeSocialTemplate} />
                <Column field="totalFaltas" header="Total Faltas" body={(rd) => <Chip label={rd.totalFaltas} style={{ backgroundColor: '#d45555', color: 'white' }} />} />
                <Column field="totalAtrasos" header="Total Atrasos" body={(rd) => <Chip label={rd.totalAtrasos} style={{ backgroundColor: '#f0b800', color: 'white' }} />} />
                <Column header="Detalhes" body={(rowData) => (
                    <Button
                        icon="pi pi-search"
                        // label="Ver Tudo" 
                        className="p-button-text"
                        onClick={() => {
                            setAlunoSelecionado(rowData);
                            setDisplayModal(true);
                        }}
                    />
                )} />
            </DataTable>

            {/* MODAL DETALHADO POR ALUNO */}
            <Dialog
                header={renderHeader()}
                visible={displayModal}
                style={{ width: '70vw' }}
                onHide={() => setDisplayModal(false)}
            >
                <DataTable value={alunoSelecionado.registros} className="p-datatable-sm">
                    <Column field="id" header="Cód" />
                    <Column field="dataOcorrencia" header="Data" body={dataOcorrenciaBodyTemplate} />
                    <Column field="atraso" header="Tipo" body={tipoBodyTemplate} />
                    <Column field="obs" header="Observação" />
                    <Column header="Ações" body={actionBodyTemplate} />
                </DataTable>
            </Dialog>
        </div>
    );
};